/* eslint  max-len: 0 */
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { showRechargeConfirmBox } from 'redux/deposit/depositSlice';
import { showBonRechargeConfirmBox } from 'redux/deposit/bonDepositSlice';
import { getProviders } from 'redux/deposit/getVoucherProvidersSlice';
import { v4 as uuidv4 } from 'uuid';
import translationKeys from 'translations/translationKeys';
import { formatCurrency } from 'components/helpers/currencyOptions';
import {
  getConversionRate,
  selectConversionRate,
  selectConversionExpiresAt,
  selectConversionLoading,
  selectConversionTargetCurrency,
  selectSecondaryCurrencyConfigs,
} from 'redux/configs/currencyConfigsSlice';
import { voucherPredefinedAmounts } from '../../../../utils/utils';
import depositwhite from '../../../../assets/images/mathys/svg/depositwhite.svg';
import ConvertedAmount from '../../navigationPanel/ConvertedAmount';
import CurrencySelector from '../../navigationPanel/CurrencySelector';

const Deposit = () => {
  const dispatch = useDispatch();
  const [bonAmount, setBonAmount] = useState('');
  const [directAmount, setDirectAmount] = useState('');
  const { t } = useTranslation();
  const [disableBon, setDisableBon] = useState(true);
  const [disableDirect, setDisableDirect] = useState(true);
  const { agentId } = useSelector((store) => store.login);
  const { currencyConfigs } = useSelector((store) => store.currencyConfigs);
  const { maxStake, stepValue } = useSelector((store) => store.betSlip);
  const { bonRechargeSuccess } = useSelector((store) => store.bonRechargeData);
  const { rechargeSuccess } = useSelector((store) => store.depositData);

  // ── Secondary currency selection / conversion ────────────────────
  const secondaryCurrencyConfigs = useSelector(selectSecondaryCurrencyConfigs);
  const conversionRate = useSelector(selectConversionRate);
  const conversionExpiresAt = useSelector(selectConversionExpiresAt);
  const conversionLoading = useSelector(selectConversionLoading);
  const conversionTargetCurrency = useSelector(selectConversionTargetCurrency);
  const currencySign = currencyConfigs?.strSymbol
  || currencyConfigs?.strCode
  || '';
  const [selectedTargetCurrency, setSelectedTargetCurrency] = useState(
    // Shared across pages so the choice made on the betslip (or here)
    // carries over. Rename the key if you'd rather keep them separate.
    () => localStorage.getItem('preferred_target_currency') || null,
  );

  // Auto-select the first secondary currency when none has been chosen yet.
  useEffect(() => {
    if (!selectedTargetCurrency && secondaryCurrencyConfigs?.length > 0) {
      const defaultCurrency = secondaryCurrencyConfigs[0].strCode;
      setSelectedTargetCurrency(defaultCurrency);
      localStorage.setItem('preferred_target_currency', defaultCurrency);
    }
  }, [secondaryCurrencyConfigs, selectedTargetCurrency]);

  const targetCurrencyConfig = (() => {
    if (!secondaryCurrencyConfigs?.length) return null;
    const valid = secondaryCurrencyConfigs.find((c) => c.strCode === selectedTargetCurrency);
    return valid || null;
  })();
  const targetCurrency = targetCurrencyConfig?.strCode || null;

  const isConversionStale = !targetCurrency ? false : (
    !conversionRate
    || conversionTargetCurrency !== targetCurrency
    || !conversionExpiresAt
    || Date.now() >= conversionExpiresAt
  );

  useEffect(() => {
    if (!targetCurrency || !currencyConfigs?.strCode) return;
    if (isConversionStale && !conversionLoading) {
      dispatch(getConversionRate({ sourceCurrency: currencyConfigs.strCode, targetCurrency }));
    }
  }, [targetCurrency, currencyConfigs, isConversionStale, conversionLoading, dispatch]);

  useEffect(() => {
    if (!conversionExpiresAt) return undefined;
    const ms = conversionExpiresAt - Date.now();
    if (ms <= 0) return undefined;
    const id = setTimeout(() => {
      if (targetCurrency && currencyConfigs?.strCode) {
        dispatch(getConversionRate({ sourceCurrency: currencyConfigs.strCode, targetCurrency }));
      }
    }, ms);
    return () => clearTimeout(id);
  }, [conversionExpiresAt, targetCurrency, currencyConfigs, dispatch]);

  const handleChange = () => {
    const bon = document.getElementById('bonAmountlabel').value;
    const code = document.getElementById('code').value;
    const amoutLable = document.getElementById('amountRDlabel').value;
    if (code !== '' && amoutLable !== '') {
      setDisableDirect(false);
    }
    if (code === '' || amoutLable === '') {
      setDisableDirect(true);
    }
    if (bon !== '') {
      setDisableBon(false);
    }
    if (bon === '') {
      setDisableBon(true);
    }
  };
  const handleButtonClick = (amt) => {
    setBonAmount(amt);
  };
  const { voucherProviders, loadingProviders } = useSelector((store) => store.voucherProviders);
  const defaultVouchers = voucherPredefinedAmounts(maxStake, stepValue);
  const arr = (voucherProviders && voucherProviders.length > 0 && voucherProviders !== undefined && voucherProviders) || defaultVouchers;
  const getConvertedString = (amount) => {
    if (!targetCurrencyConfig || !conversionRate) return null;
    try {
      return `≈ ${formatCurrency(Number(amount) * Number(conversionRate), targetCurrencyConfig)}`;
    } catch {
      return null;
    }
  };

  const handleBonRecharge = (e) => {
    e.preventDefault();
    dispatch(showBonRechargeConfirmBox({
      convertedAmount: getConvertedString(bonAmount),
    }));
  };

  const handleChangeRecharge = (e) => {
    e.preventDefault();
    const amt = e.target.elements.amountRDlabel.value;
    const acc = e.target.elements.code.value;
    if (amt === '0' || acc === '') {
      return;
    }
    dispatch(showRechargeConfirmBox({
      amt,
      acc,
      convertedAmount: getConvertedString(amt),
    }));
  };
  const {
    msg21, msg22, msg133, msg121, msg54, msg55, msg56, msg4, msg134,
  } = translationKeys;

  // ── Fetch voucher providers on mount if not already loaded ───────
  useEffect(() => {
    if (agentId && !voucherProviders?.length) {
      dispatch(getProviders(agentId));
    }
  }, [agentId, voucherProviders, dispatch]);

  useEffect(() => {
    if (bonRechargeSuccess || rechargeSuccess) {
      setBonAmount('');
      setDirectAmount('');
      setDisableBon(true);
      document.getElementById('voucher-form').reset();
      document.getElementById('direct-refill-form').reset();
    }
  }, [bonRechargeSuccess, rechargeSuccess]);

  return (
    <div className="details history">
      <div className="header">
        <img src={depositwhite} alt="" />
        <span>{t(msg21)}</span>
      </div>
      <div className="content">
        <ul className="nav nav-tabs" id="myTab" role="tablist">
          <li className="radio-option" role="presentation">
            <input type="radio" name="tab" id="home-tab" data-bs-toggle="tab" data-bs-target="#home-tab-pane" role="tab" aria-controls="home-tab-pane" aria-selected="true" defaultChecked />
            <label htmlFor="home-tab">{t(msg133)}</label>
          </li>
          <li className="radio-option" role="presentation">
            <input type="radio" name="tab" id="profile-tab" data-bs-toggle="tab" data-bs-target="#profile-tab-pane" role="tab" aria-controls="profile-tab-pane" aria-selected="false" />
            <label htmlFor="profile-tab">{t(msg121)}</label>
          </li>
        </ul>
        <div className="tab-content" id="myTabContent">
          {secondaryCurrencyConfigs?.length > 0 && (
          <CurrencySelector
            secondaryCurrencyConfigs={secondaryCurrencyConfigs}
            targetCurrency={targetCurrency}
            onChange={(code) => {
              setSelectedTargetCurrency(code || null);
              if (code) localStorage.setItem('preferred_target_currency', code);
              else localStorage.removeItem('preferred_target_currency');
            }}
            variant="inline"
            id="deposit-currency"
          />
          )}
          <div className="tab-pane fade show active" id="home-tab-pane" role="tabpanel" aria-labelledby="home-tab" tabIndex="0">
            <form id="voucher-form" action="" onSubmit={handleBonRecharge} className="recharge-form">
              <div className="content-grid">
                <div className="input-wrap">
                  <label htmlFor="bonAmountlabel" className="form-label">
                    {t(msg54)}
                  </label>
                  <div className="amount-input-group">
                    <span className="amount-input-group__sign">{currencySign}</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="form-control deposit-input"
                      id="bonAmountlabel"
                      placeholder={t(msg134)}
                      required
                      onKeyDown={(e) => {
                        const allowed = /^\d$/.test(e.key)
                          || e.key === 'Backspace'
                          || e.key === 'Delete'
                          || e.key === '.'
                          || e.key === 'Tab'
                          || e.key === 'ArrowLeft'
                          || e.key === 'ArrowRight';
                        if (!allowed) e.preventDefault();
                        // allow only one decimal separator
                        if ((e.key === '.' || e.key === ',') && bonAmount.includes('.')) {
                          e.preventDefault();
                        }
                      }}
                      value={bonAmount}
                      onChange={(e) => {
                        // normalise comma → dot
                        const raw = e.target.value.replace(',', '.');
                        // reject anything that isn't a valid partial decimal
                        if (!/^\d*\.?\d*$/.test(raw)) return;
                        setBonAmount(raw);
                        handleChange();
                        localStorage.setItem('couponAmt', JSON.stringify(raw));
                      }}
                    />
                  </div>
                  {targetCurrencyConfig && (
                  <div className="amount-conversion">
                    <ConvertedAmount
                      amount={Number(bonAmount) || 0}
                      rate={conversionRate}
                      loading={conversionLoading}
                      targetCurrencyConfig={targetCurrencyConfig}
                    />
                  </div>
                  )}
                </div>
                <div className="block bl-multiple amounts">
                  {loadingProviders && (
                    <div className="loader-container">
                      <div className="spinner-border large" role="status">
                        <span className="visually-hidden">{t(msg22)}</span>
                      </div>
                      <span>{t(msg22)}</span>
                    </div>
                  )}
                  {!loadingProviders && arr?.length > 0 && arr.map((predefinedAmount) => predefinedAmount && (
                    <button
                      key={uuidv4()}
                      id={predefinedAmount}
                      type="button"
                      className={`btn btn-secondary${predefinedAmount === bonAmount ? ' active' : ''}`}
                      onClick={() => {
                        setDisableBon(false);
                        handleButtonClick(predefinedAmount);
                        localStorage.setItem('couponAmt', JSON.stringify(predefinedAmount));
                      }}
                    >
                      <span>{formatCurrency(predefinedAmount, currencyConfigs)}</span>
                      {targetCurrencyConfig && (
                        <ConvertedAmount
                          amount={Number(predefinedAmount) || 0}
                          rate={conversionRate}
                          loading={conversionLoading}
                          targetCurrencyConfig={targetCurrencyConfig}
                        />
                      )}
                    </button>
                  ))}
                </div>
                <button
                  type="submit"
                  className="btn btn-secondary"
                  disabled={disableBon}
                >
                  <span>{t(msg55)}</span>
                </button>
              </div>
            </form>
          </div>
          <div className="tab-pane fade" id="profile-tab-pane" role="tabpanel" aria-labelledby="profile-tab" tabIndex="0">
            <form id="direct-refill-form" action="" onSubmit={handleChangeRecharge} className="recharge-form">
              <div className="content-grid">
                <div className="input-wrap">
                  <label htmlFor="code" className="form-label">
                    {t(msg56)}
                  </label>
                  <input
                    type="text"
                    className="form-control deposit-input"
                    id="code"
                    placeholder={t(msg4)}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="input-wrap">
                  <label htmlFor="amountRDlabel" className="form-label">
                    {t(msg54)}
                  </label>
                  <div className="amount-input-group">
                    <span className="amount-input-group__sign">{currencySign}</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="form-control deposit-input"
                      id="amountRDlabel"
                      placeholder={t(msg134)}
                      required
                      onKeyDown={(e) => {
                        const allowed = /^\d$/.test(e.key)
                          || e.key === 'Backspace'
                          || e.key === 'Delete'
                          || e.key === '.'
                          || e.key === 'Tab'
                          || e.key === 'ArrowLeft'
                          || e.key === 'ArrowRight';
                        if (!allowed) e.preventDefault();
                        if ((e.key === '.' || e.key === ',') && directAmount.includes('.')) {
                          e.preventDefault();
                        }
                      }}
                      value={directAmount}
                      onChange={(e) => {
                        const raw = e.target.value.replace(',', '.');
                        if (!/^\d*\.?\d*$/.test(raw)) return;
                        setDirectAmount(raw);
                        handleChange();
                      }}
                    />
                  </div>
                  {targetCurrencyConfig && (
                  <div className="amount-conversion">
                    <ConvertedAmount
                      amount={Number(directAmount) || 0}
                      rate={conversionRate}
                      loading={conversionLoading}
                      targetCurrencyConfig={targetCurrencyConfig}
                    />
                  </div>
                  )}
                </div>
                <button
                  type="submit"
                  className="btn btn-secondary"
                  disabled={disableDirect}
                >
                  <span>{t(msg55)}</span>
                </button>
              </div>
            </form>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Deposit;
